import { useState } from "react";
import { FileDown, Tag, Truck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

const carrierOf = (o) => o.carrier || (o.shipping_method === "mondial_relay" ? "mondial_relay" : "colissimo");
const SHIP_MAIL = { sent: "Suivi envoyé à la cliente", sending: "Envoi du suivi…", failed: "Email de suivi échoué", no_email: "Pas d'email cliente" };

function ShipDialog({ o, open, onOpenChange, onDone }) {
  const [num, setNum] = useState(o.tracking_number || "");
  const [carrier, setCarrier] = useState(carrierOf(o));
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/orders/${o.id}`, { fulfillment: "expédiée", tracking_number: num, carrier });
      toast.success("Commande expédiée — email de suivi envoyé");
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const input = "h-11 w-full rounded-sm border border-[var(--line)] bg-[var(--ink)] px-3 text-sm outline-none focus:border-[var(--gold)]";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`ship-dialog-${o.id}`} className="max-w-md border-[var(--line)] bg-[var(--ink)] text-[var(--parchment)]">
        <DialogHeader><DialogTitle className="font-display text-2xl italic">Marquer comme expédiée</DialogTitle></DialogHeader>
        <select data-testid="ship-carrier" value={carrier} onChange={(e) => setCarrier(e.target.value)} className={input}>
          <option value="mondial_relay">Mondial Relay</option>
          <option value="colissimo">Colissimo</option>
        </select>
        <input data-testid="ship-tracking-input" value={num} onChange={(e) => setNum(e.target.value)} placeholder="Numéro de suivi" className={input} />
        <p className="text-xs text-[var(--muted)]">La cliente reçoit automatiquement un email avec son numéro et le lien de suivi.</p>
        <button data-testid="ship-submit" disabled={!num.trim() || busy} onClick={submit} className="flex h-12 items-center justify-center gap-2 rounded-full bg-[var(--gold)] text-xs font-semibold uppercase tracking-[0.2em] text-[var(--ink)] disabled:opacity-40">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Expédier & envoyer le suivi
        </button>
      </DialogContent>
    </Dialog>
  );
}

export function ShipCell({ o, config, onDone }) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const carrier = carrierOf(o);
  const ready = carrier === "mondial_relay" ? config?.mondial_relay : config?.colissimo;
  const paid = o.payment_status === "paid";

  const label = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/orders/${o.id}/label`);
      toast.success("Étiquette créée");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const download = async () => {
    try {
      const r = await api.get(`/admin/orders/${o.id}/label.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const btn = "flex items-center gap-1.5 rounded-full border border-[var(--line)] px-3 py-1.5 text-xs transition-colors hover:border-[var(--gold)] disabled:opacity-40";

  return (
    <div className="flex min-w-[190px] flex-col gap-2">
      {o.tracking_number ? <span data-testid={`admin-order-tracking-${o.id}`} className="text-xs text-[var(--gold-soft)]">Suivi : {o.tracking_number}</span> : null}
      {o.shipped_email_status ? <span className="text-xs text-[var(--muted)]">{SHIP_MAIL[o.shipped_email_status] || o.shipped_email_status}</span> : null}
      <div className="flex flex-wrap gap-2">
        {o.label_path ? (
          <button data-testid={`admin-order-label-download-${o.id}`} onClick={download} className={btn}><FileDown className="h-3.5 w-3.5" /> Étiquette PDF</button>
        ) : (
          <button data-testid={`admin-order-label-${o.id}`} disabled={!paid || busy || !ready} title={ready ? "" : "Accès transporteur non configurés"} onClick={label} className={btn}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Tag className="h-3.5 w-3.5" />} {ready ? "Générer l'étiquette" : "Étiquette (accès requis)"}
          </button>
        )}
        {paid && o.fulfillment !== "expédiée" && o.fulfillment !== "livrée" && (
          <button data-testid={`admin-order-ship-${o.id}`} onClick={() => setOpen(true)} className={btn}><Truck className="h-3.5 w-3.5" /> Expédier</button>
        )}
      </div>
      <ShipDialog o={o} open={open} onOpenChange={setOpen} onDone={onDone} />
    </div>
  );
}
